import React, { useMemo, useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ChatChart = ({ data, title = "圖表數據", question = "", chartType = "bar" ,xlabel, ylabel}) => {
  // 解析 ylabel 中的單位
  const parseUnitFromYLabel = (ylabel) => {
    if (!ylabel) return null;
    const unitMatch = ylabel.match(/[（(]([^）)]+)[）)]$/u);
    return unitMatch ? unitMatch[1] : null;
  };

  const unit = parseUnitFromYLabel(ylabel);
  
  const finalChartType = useMemo(() => {
    return chartType ;
  }, [chartType]);
  
  // 響應式圓餅圖半徑和圖表高度
  const [pieRadius, setPieRadius] = useState(160);
  const [chartHeight, setChartHeight] = useState('500px');
  
  useEffect(() => {
    const updateChartDimensions = () => {
      if (typeof window === 'undefined') return;
      const width = window.innerWidth;
      
      if (width < 480) {
        setPieRadius(60);      // 手機
        setChartHeight('300px');
        console.log('Setting mobile dimensions: radius=60, height=300px');
      } else if (width < 820) {
        setPieRadius(100);      // 平板
        setChartHeight('400px');
        console.log('Setting tablet dimensions: radius=80, height=400px');
      } else {
        setPieRadius(160);      // 桌面
        setChartHeight('500px');
        console.log('Setting desktop dimensions: radius=100, height=500px');
      }

      
    };
    
    updateChartDimensions();
    window.addEventListener('resize', updateChartDimensions);
    return () => window.removeEventListener('resize', updateChartDimensions);
  }, []);

  // 預設顏色庫
  const COLORS = ['#19c37d', '#2563eb', '#0078d4', '#4285f4', '#d97706', '#6b7280', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

  // 為數據添加顏色
  const dataWithColors = data.map((item, index) => ({
    ...item,
    color: item.color || COLORS[index % COLORS.length]
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      const percentage = totalValue > 0 ? ((value / totalValue) * 100).toFixed(1) : 0;
      
      return (
        <div style={{
          backgroundColor: 'var(--chart-tooltip-bg)',
          padding: '12px',
          borderRadius: '8px',
          border: '1px solid var(--chart-tooltip-border)',
          color: 'var(--text)'
        }}>
          <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
            {payload[0].label || payload[0].payload.label}
          </p>
          <p style={{ margin: '0', color: 'var(--chart-title)' }}>
            {finalChartType === 'pie' ? `${percentage}%` : `${value}${unit ? ` ${unit}` : ''}`}
          </p>
        </div>
      );
    }
    return null;
  };

  // 計算總和用於百分比轉換
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);
  
  const renderPieLabel = (entry) => {
    const percentage = totalValue > 0 ? ((entry.value / totalValue) * 100).toFixed(1) : 0;
    return `${entry.label} (${percentage}%)`;
  };

  return (
    <div style={{
      border: '1px solid var(--chart-panel-border)',
      borderRadius: '8px',
      padding: '16px',
      margin: '8px 0',
      backgroundColor: 'var(--chart-panel-bg)'
    }}>
      <div style={{ marginBottom: '16px' }}>
        <h4 style={{ 
          margin: '0 0 8px 0', 
          color: 'var(--chart-title)',
          fontSize: '16px',
          fontWeight: 'bold'
        }}>
          📊 {title}
        </h4>
      </div>

      {/* 圖表顯示 */}
      <div style={{ 
        height: chartHeight, 
        marginBottom: '16px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <ResponsiveContainer width="100%" height="100%">
          {finalChartType === 'bar' ? (
            <BarChart
              data={dataWithColors}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
              <XAxis 
                dataKey="label" 
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fill: 'var(--chart-axis)', fontSize: 12 }}
                label={{ value: xlabel || '類別', angle: 0, position: 'insideBottom', style: { fill: 'var(--chart-axis)' } }}
              />
              <YAxis 
                label={{ value: ylabel || '數值', angle: -90, position: 'insideLeft', style: { fill: 'var(--chart-axis)' } }}
                tick={{ fill: 'var(--chart-axis)' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {dataWithColors.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          ) : finalChartType === 'line' ? (
            <LineChart
              data={dataWithColors}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
              <XAxis 
                dataKey="label" 
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fill: 'var(--chart-axis)', fontSize: 12 }}
                label={{ value: xlabel || '類別', angle: 0, position: 'insideBottom', style: { fill: 'var(--chart-axis)' } }}
              />
              <YAxis 
                label={{ value: ylabel || '數值', angle: -90, position: 'insideLeft', style: { fill: 'var(--chart-axis)' } }}
                tick={{ fill: 'var(--chart-axis)' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="value" 
                stroke="var(--chart-title)"
                strokeWidth={3}
                dot={{ fill: 'var(--chart-title)', strokeWidth: 2, r: 6 }}
                activeDot={{ r: 8, stroke: 'var(--chart-title)', strokeWidth: 2 }}
              />
            </LineChart>
          ) : (
            <PieChart>
              <Pie
                data={dataWithColors}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={renderPieLabel}
                outerRadius={pieRadius}
                innerRadius={pieRadius * 0.3}
                fill="#8884d8"
                dataKey="value"
              >
                {dataWithColors.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* 數據列表 */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '8px',
        marginBottom: '12px'
      }}>
        {dataWithColors.map((item, index) => (
          <div key={index} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 8px',
            backgroundColor: 'var(--chart-chip-bg)',
            borderRadius: '4px',
            border: '1px solid var(--chart-chip-border)'
          }}>
            <div 
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                backgroundColor: item.color,
                flexShrink: 0
              }}
            />
            <span style={{ 
              fontSize: '12px', 
              color: 'var(--text)'
            }}>
              <span style={{ fontWeight: 'bold' }}>{item.label}</span>:               {
                finalChartType === 'pie' 
                  ? `${totalValue > 0 ? ((item.value / totalValue) * 100).toFixed(1) : 0}%`
                  : `${item.value}${unit ? ` ${unit}` : ''}`
              }
            </span>
          </div>
        ))}
      </div>

      {/* 數據統計 */}
      <div style={{
        padding: '12px',
        backgroundColor: 'var(--chart-stats-bg)',
        borderRadius: '4px',
        border: '1px solid var(--chart-panel-border)'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '16px',
          textAlign: 'center'
        }}>
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'var(--chart-muted)' }}>總項目數</p>
            <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: 'var(--text)' }}>
              {data.length} 筆
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'var(--chart-muted)' }}>總和</p>
            <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: 'var(--text)' }}>
              {finalChartType === 'pie' ? '100%' : `${data.reduce((sum, item) => sum + item.value, 0).toFixed(1)}${unit ? ` ${unit}` : ''}`}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'var(--chart-muted)' }}>最高值</p>
            <p style={{ margin: '0', fontSize: '18px', fontWeight: 'bold', color: 'var(--text)' }}>
              {finalChartType === 'pie' 
                ? `${totalValue > 0 ? ((Math.max(...data.map(item => item.value)) / totalValue) * 100).toFixed(1) : 0}%`
                : `${Math.max(...data.map(item => item.value))}${unit ? ` ${unit}` : ''}`
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatChart;
